import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import { resetPasswordErrorMessage } from '@/utils/resetPasswordErrorMessage';
import { useT } from '@/i18n/labels';
import { useThemeStore } from '@/stores/themeStore';
import { dirFor } from '@/stores/langStore';
import { makeTokens } from '@/theme/tokens';

const MIN_PASSWORD_LENGTH = 8;

// Plain-text nav link styled to sit under the card's action button. Inline
// style (not a new CSS class) so it stays within the "reuse existing classes
// only" rule for this task while still reading as a link, not a button.
const linkButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'var(--accent)',
  fontSize: 13.5,
  fontWeight: 600,
  cursor: 'pointer',
  textDecoration: 'underline',
};

/**
 * Public / pre-auth. Reached from the "reset your password" email link:
 * `/reset-password?token=...&tenant=...` (ADR-012, WS-5.A). Validates the
 * token (non-consuming) on mount before rendering the form — a stale/used/
 * expired link shows an explicit "invalid or expired" state instead of a
 * form the submit would just reject anyway. Renders outside <ConsoleShell/>
 * (mirrors LoginPage).
 */
export const ResetPasswordPage: React.FC = () => {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = useThemeStore((s) => s.mode);
  const tokens = useMemo(() => makeTokens({ mode }), [mode]);

  const token = searchParams.get('token') ?? '';
  const tenantId = searchParams.get('tenant') ?? '';

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token || !tenantId) {
      setValidating(false);
      setTokenValid(false);
      return;
    }
    axiosClient
      .post<{ valid: boolean }>(API.AUTH.VALIDATE_ACTION_TOKEN, { tenantId, token, type: 'reset' })
      .then(({ data }) => {
        if (!cancelled) setTokenValid(data.valid);
      })
      .catch(() => {
        if (!cancelled) setTokenValid(false);
      })
      .finally(() => {
        if (!cancelled) setValidating(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, tenantId]);

  const validate = (): string => {
    if (next.length < MIN_PASSWORD_LENGTH) {
      return t('resetPwMinLength', { n: MIN_PASSWORD_LENGTH });
    }
    if (next !== confirm) {
      return t('resetPwMismatch');
    }
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setLoading(true);
    try {
      await axiosClient.post(API.AUTH.RESET_PASSWORD, { tenantId, token, newPassword: next });
      setSuccess(true);
    } catch (err) {
      setError(resetPasswordErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="ac-login" dir={dirFor(lang)} style={tokens as React.CSSProperties}>
        <div className="ac-card ac-login-card">
          <p style={{ textAlign: 'center' }}>{t('resetPwValidating')}</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="ac-login" dir={dirFor(lang)} style={tokens as React.CSSProperties}>
        <div className="ac-card ac-login-card" data-testid="reset-password-invalid">
          <div className="ac-login-title">{t('resetPwInvalidLinkTitle')}</div>
          <div className="ac-login-sub">{t('resetPwInvalidLinkMessage')}</div>
          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            className="ac-btn primary"
            style={{ width: '100%' }}
          >
            {t('resetPwRequestNewLink')}
          </button>
          <p style={{ marginTop: 14, marginBottom: 0, textAlign: 'center' }}>
            <button type="button" onClick={() => navigate('/login')} style={linkButtonStyle}>
              {t('forgotPwBackToLogin')}
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="ac-login" dir={dirFor(lang)} style={tokens as React.CSSProperties}>
      <form onSubmit={handleSubmit} className="ac-card ac-login-card" data-testid="reset-password-form">
        <div className="ac-login-title">{t('resetPwTitle')}</div>
        <div className="ac-login-sub">{t('resetPwSubtitle')}</div>

        {error && (
          <div role="alert" className="ac-login-err">
            {error}
          </div>
        )}

        {success ? (
          <>
            <p data-testid="reset-password-success">{t('resetPwSuccess')}</p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="ac-btn primary"
              style={{ width: '100%' }}
            >
              {t('resetPwGoToLogin')}
            </button>
          </>
        ) : (
          <>
            <div className="ac-set-field-group" style={{ marginBottom: 14 }}>
              <label htmlFor="newPassword" className="ac-set-field-label">
                {t('resetPwNewPassword')}
              </label>
              <input
                id="newPassword"
                type="password"
                className="ac-field"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>

            <div className="ac-set-field-group" style={{ marginBottom: 22 }}>
              <label htmlFor="confirmPassword" className="ac-set-field-label">
                {t('resetPwConfirmPassword')}
              </label>
              <input
                id="confirmPassword"
                type="password"
                className="ac-field"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>

            <button type="submit" disabled={loading} className="ac-btn primary" style={{ width: '100%' }}>
              {loading ? t('resetPwSubmitting') : t('resetPwSubmit')}
            </button>
          </>
        )}
      </form>
    </div>
  );
};

export default ResetPasswordPage;
